import { useCallback, useEffect, useRef } from 'react'

/**
 * Tracks mouse position on a container and writes CSS custom properties
 * --spotlight-x and --spotlight-y directly to the element (no re-renders).
 */
export function useMouseSpotlight<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null)
  const raf = useRef(0)

  const handleMove = useCallback((e: MouseEvent) => {
    if (raf.current) return
    raf.current = requestAnimationFrame(() => {
      const el = ref.current
      if (!el) {
        raf.current = 0
        return
      }
      el.style.setProperty('--spotlight-x', `${e.clientX}px`)
      el.style.setProperty('--spotlight-y', `${e.clientY}px`)
      raf.current = 0
    })
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    window.addEventListener('mousemove', handleMove, { passive: true })
    return () => {
      window.removeEventListener('mousemove', handleMove)
      if (raf.current) cancelAnimationFrame(raf.current)
    }
  }, [handleMove])

  return { ref }
}
